<script>
import baseMixin from '@/mixins/baseMixin'
import $dayjs from '@/services/date'
import { TimesheetOperationType } from '@/shared/constants/timesheet/TimesheetOperationType'
import generateBreakMinutesList from '@/utils/generate-break-minutes-list.js'
import { RateUnitType } from '@/shared/constants/booking/RateUnitType'
import { TimesheetMethod } from '@/shared/constants/timesheet/TimesheetMethod'
import TimesheetCandidateDisputeStatusAlert from './timesheet-candidate-dispute-status-alert.vue'

export default {
  name: 'TimeSheetChangeTimeCard',
  components: { TimesheetCandidateDisputeStatusAlert },
  mixins: [baseMixin],
  props: {
    timesheet: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      startTime: '',
      endTime: '',
      payOptionType: '',
      isValid: true,
      rules: {
        required: (v) => !!v || this.$t('input.inputRequiredValidationText'),
        startTimeAfterEnd: () =>
          this.startTime
            ? !this.isStartInvalid ||
              this.$t('input.startTimeBeforeEndValidationText')
            : true,
        endTimeBeforeStart: () =>
          this.endTime
            ? !this.isEndInvalid ||
              this.$t('input.endTimeBeforeStartValidationText')
            : true,
      },
      TimesheetOperationType,
      breaks: [],
      breakMinutes: 0,
      selectedBreakMinutesValue: null,
    }
  },
  computed: {
    isStartInvalid() {
      return this.startTime > this.endTime
    },
    isEndInvalid() {
      return this.endTime < this.startTime
    },
    isUnitClassification() {
      return this.timesheet?.unitType === RateUnitType.UNIT
    },
    isHourlyClassification() {
      return this.timesheet?.unitType === RateUnitType.HOURLY
    },
    isCandidateTimesheeting() {
      return this.timesheet?.timesheetMethod === TimesheetMethod.candidate
    },
    canUpdateBreaks() {
      return this.isHourlyClassification || this.isCandidateTimesheeting
    },
  },
  created() {
    this.startTime = $dayjs(this.timesheet.startTimeUTC)
      .tz(this.timesheet.locationTimeZone)
      .format('HH:mm')
    this.endTime = $dayjs(this.timesheet.endTimeUTC)
      .tz(this.timesheet.locationTimeZone)
      .format('HH:mm')
    this.payOptionType = this.timesheet.payOptionType

    if (this.canUpdateBreaks) this.initBreaksList(this.timesheet?.breakMinutes)
  },
  methods: {
    validate() {
      return this.$refs.timeChangeForm.validate()
    },
    save() {
      this.isValid = this.validate()
      if (!this.isValid) return false

      this.timesheet.operationType = TimesheetOperationType.MODIFY
      this.timesheet.newStartTimeLocal = this.startTime
      this.timesheet.newEndTimeLocal = this.endTime
      this.timesheet.newPayOptionType = this.payOptionType

      // Ensure the existing break minutes on the booking are returned
      // if minutes aren't updated. If breakMinutes was null, it'll be
      // returns as 0
      if (this.canUpdateBreaks) {
        this.timesheet.newBreakMinutes =
          this.timesheet.newBreakMinutes >= 0 &&
          this.timesheet.newBreakMinutes !== null
            ? this.timesheet.newBreakMinutes
            : this.timesheet.breakMinutes
      }

      this.$emit('saveChanges', this.timesheet)

      this.close()
    },
    updateBreaks(value) {
      this.timesheet.newBreakMinutes = value
    },
    close() {
      this.$emit('closeModal')
    },
    initBreaksList(initBreaks = 0) {
      if (!this.canUpdateBreaks) return
      if (!this.startTime || !this.endTime) return

      this.breaks = generateBreakMinutesList(this.startTime, this.endTime)

      if (initBreaks > 0) {
        const foundBreak = this.breaks.find((x) => x.id === initBreaks)
        if (foundBreak) return (this.breakMinutes = foundBreak.id)
      }

      // Initialise breaks at 0 mins when initialising the breaks list
      this.breakMinutes = this.breaks[0].id
      this.timesheet.newBreakMinutes = this.breaks[0].id
    },
  },
}
</script>

<template>
  <v-card>
    <v-card-title class="text-h6">{{
      $t('timesheets.timesheetChangeTimeTitle')
    }}</v-card-title>
    <v-card-subtitle>{{
      $t('timesheets.timesheetChangeTimeSubtitle')
    }}</v-card-subtitle>
    <v-card-text>
      <TimesheetCandidateDisputeStatusAlert :timesheet="timesheet" />

      <v-form
        ref="timeChangeForm"
        lazy-validation
        :value="isValid"
        autocomplete="off"
      >
        <div id="start-time-input_container">
          <v-text-field
            id="start-time-input"
            v-model="startTime"
            type="time"
            color="indigo"
            :label="$t('input.startTimeLabel')"
            :rules="[rules.startTimeAfterEnd]"
          ></v-text-field>
        </div>

        <div id="end-time-input_container">
          <v-text-field
            id="end-time-input"
            v-model="endTime"
            type="time"
            color="indigo"
            :label="$t('input.endTimeLabel')"
            :rules="[rules.endTimeBeforeStart]"
          ></v-text-field>
        </div>

        <section v-if="canUpdateBreaks">
          <v-select
            id="create-booking-form__breaks-select"
            ref="create-booking-form__breaks-select"
            :label="$t('timesheets.breaksLabel')"
            :value="breakMinutes"
            :items="breaks"
            item-text="text"
            item-value="id"
            :return-object="false"
            @change="updateBreaks"
          />
        </section>

        <div id="pay-option-types-select_container">
          <v-select
            v-model="payOptionType"
            label="Pay Options"
            :items="timesheet.payOptionTypes"
          >
          </v-select>
        </div>
      </v-form>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="close">{{
        $t('timesheets.timesheetChangeTimeCancelButtonText')
      }}</v-btn>
      <v-btn text color="blue" :disabled="!isValid" @click="save">
        {{ $t('timesheets.timesheetChangeTimeUpdateButtonText') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
